import React, { useEffect, useState } from 'react';
import { settingsAPI } from '../api/endpoints';
import './SettingsManager.css';

type Role = 'admin' | 'manager' | 'viewer';
interface GroupRole { group: string; role: Role; }
interface SsoSettings {
  ssoRestrictByGroups: boolean;
  ssoGroupRoles: GroupRole[];
  ssoDefaultRole: Role;
  publicBaseUrl: string;
  publicDisplayPath: string;
}

const ROLES: Role[] = ['admin', 'manager', 'viewer'];
const ROLE_LABEL: Record<Role, string> = { admin: 'Administrator', manager: 'Manager', viewer: 'Betrachter' };

export const SettingsManager: React.FC = () => {
  const [settings, setSettings] = useState<SsoSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      const res = await settingsAPI.getSso();
      const d = res.data.data;
      setSettings({
        ssoRestrictByGroups: d.ssoRestrictByGroups,
        ssoGroupRoles: d.ssoGroupRoles || [],
        ssoDefaultRole: d.ssoDefaultRole || 'viewer',
        publicBaseUrl: d.publicBaseUrl || '',
        publicDisplayPath: d.publicDisplayPath || '/display',
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (patch: Partial<SsoSettings>) => {
    setSettings(s => (s ? { ...s, ...patch } : s));
    setSaved(false);
  };
  const updateRow = (i: number, field: keyof GroupRole, value: string) => {
    if (!settings) return;
    update({ ssoGroupRoles: settings.ssoGroupRoles.map((r, idx) => idx === i ? { ...r, [field]: value } : r) });
  };
  const addRow = () => settings && update({ ssoGroupRoles: [...settings.ssoGroupRoles, { group: '', role: 'viewer' }] });
  const removeRow = (i: number) => settings && update({ ssoGroupRoles: settings.ssoGroupRoles.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setSaved(false);
    try {
      await settingsAPI.updateSso(settings);
      setSaved(true);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="settings-page"><p className="settings-empty">Laden…</p></div>;
  if (!settings) return <div className="settings-page"><p className="settings-empty">Einstellungen nicht verfügbar.</p></div>;

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h1>Einstellungen</h1>
          <p className="settings-sub">SSO-Zugriff & Rollen-Zuordnung (authentik-Gruppen).</p>
        </div>
      </div>

      <div className="settings-card">
        <h3>Öffentliche URLs</h3>
        <p className="settings-hint" style={{ marginTop: 0, marginBottom: 18 }}>
          Basis für die kopierbaren Display-Links (BrightSign &amp; Co.). Leer lassen, um die aktuelle Adresse des Browsers zu verwenden.
        </p>
        <label className="settings-field" style={{ maxWidth: 520 }}>
          <span>Öffentliche Basis-URL</span>
          <input
            value={settings.publicBaseUrl}
            onChange={e => update({ publicBaseUrl: e.target.value })}
            placeholder="https://displaycms.it.nsudz.de"
          />
        </label>
        <label className="settings-field" style={{ maxWidth: 520 }}>
          <span>Display-Pfad</span>
          <input
            value={settings.publicDisplayPath}
            onChange={e => update({ publicDisplayPath: e.target.value })}
            placeholder="/display"
          />
        </label>
        <div className="settings-preview">
          Beispiel-Display-Link:&nbsp;
          <code>{(settings.publicBaseUrl || 'https://…') + (settings.publicDisplayPath || '/display') + '/?display=<ID>'}</code>
        </div>
        <div className="settings-actions">
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
          {saved && <span className="settings-saved">✓ Gespeichert</span>}
        </div>
      </div>

      <div className="settings-card">
        <h3>SSO-Zugriffssteuerung</h3>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={settings.ssoRestrictByGroups}
            onChange={e => update({ ssoRestrictByGroups: e.target.checked })}
          />
          <span>Nur Mitglieder der unten gelisteten Gruppen dürfen sich per SSO anmelden</span>
        </label>

        <div className="group-roles">
          <div className="group-roles-head">
            <span>authentik-Gruppe</span><span>CMS-Rolle</span><span></span>
          </div>
          {settings.ssoGroupRoles.length === 0 && (
            <p className="settings-empty">Noch keine Gruppen zugeordnet.</p>
          )}
          {settings.ssoGroupRoles.map((r, i) => (
            <div key={i} className="group-role-row">
              <input
                value={r.group}
                onChange={e => updateRow(i, 'group', e.target.value)}
                placeholder="z.B. admin"
              />
              <select value={r.role} onChange={e => updateRow(i, 'role', e.target.value)}>
                {ROLES.map(role => <option key={role} value={role}>{ROLE_LABEL[role]}</option>)}
              </select>
              <button className="btn-tiny btn-danger" onClick={() => removeRow(i)} title="Entfernen">🗑️</button>
            </div>
          ))}
          <button className="btn-secondary add-group" onClick={addRow}>+ Gruppe hinzufügen</button>
        </div>

        <label className="settings-field">
          <span>Standard-Rolle {settings.ssoRestrictByGroups ? '(ungenutzt, da eingeschränkt)' : '(wenn keine Gruppe passt)'}</span>
          <select
            value={settings.ssoDefaultRole}
            onChange={e => update({ ssoDefaultRole: e.target.value as Role })}
            disabled={settings.ssoRestrictByGroups}
          >
            {ROLES.map(role => <option key={role} value={role}>{ROLE_LABEL[role]}</option>)}
          </select>
        </label>

        <div className="settings-actions">
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
          {saved && <span className="settings-saved">✓ Gespeichert</span>}
        </div>

        <p className="settings-hint">
          Die Rolle wird bei <strong>jedem</strong> SSO-Login aus der Gruppenmitgliedschaft neu gesetzt.
          Bei mehreren passenden Gruppen gewinnt die höchste Rolle (Administrator&nbsp;&gt;&nbsp;Manager&nbsp;&gt;&nbsp;Betrachter).
          Die authentik-Anwendung muss den <code>groups</code>-Claim im Profil-Scope liefern.
        </p>
      </div>
    </div>
  );
};
